/**
 * UserExport Component
 *
 * Provides CSV and Excel export functionality for filtered user lists
 * Respects current filters and search criteria
 */

import React, { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { User } from '../../pages/PlatformUsers';
import { csvRow } from '@/utils/csv';

interface UserExportProps {
  users: User[];
  totalUsers: number;
  filterActive: boolean;
}

export const UserExport: React.FC<UserExportProps> = ({
  users,
  totalUsers,
  filterActive
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('csv');
  const [includeOptionalColumns, setIncludeOptionalColumns] = useState(false);
  const [exporting, setExporting] = useState(false);

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const formatDateTime = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  const generateCSV = () => {
    const headers = [
      'Email',
      'First Name',
      'Last Name',
      'Role',
      'Organization',
      'Status',
      'Created At',
      'Last Login'
    ];

    if (includeOptionalColumns) {
      headers.push('Phone', 'Department', 'Specialties', 'Medical License');
    }

    const rows = users.map(user => {
      const baseRow = [
        user.email,
        user.firstName,
        user.lastName,
        user.role,
        user.organizationName || '',
        user.isActive ? 'Active' : 'Inactive',
        formatDate(user.createdAt),
        formatDateTime(user.lastLogin)
      ];

      if (includeOptionalColumns) {
        baseRow.push(
          user.profile?.phoneNumber || '-',
          user.profile?.department || '-',
          user.profile?.specialties?.join('; ') || '-',
          user.practitioner?.medicalLicense || '-'
        );
      }

      return baseRow;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => csvRow(row))
    ].join('\n');

    return csvContent;
  };

  const downloadCSV = () => {
    setExporting(true);
    try {
      const csvContent = generateCSV();
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `users-export-${timestamp}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowPreview(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const downloadExcel = () => {
    setExporting(true);
    try {
      // For Excel, we'll use CSV with .xls extension
      // Modern Excel opens CSV files correctly
      const csvContent = generateCSV();
      const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `users-export-${timestamp}.xls`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowPreview(false);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExport = () => {
    if (exportFormat === 'csv') {
      downloadCSV();
    } else {
      downloadExcel();
    }
  };

  const previewUsers = users.slice(0, 10);

  return (
    <>
      {/* Export Button */}
      <button
        onClick={() => setShowPreview(true)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        disabled={users.length === 0}
      >
        <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
        Export ({users.length})
      </button>

      {/* Export Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Export Users</h2>
              <p className="mt-1 text-sm text-gray-600">
                {filterActive
                  ? `Exporting ${users.length} filtered users (${totalUsers} total)`
                  : `Exporting all ${users.length} users`}
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Export Format
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={(e) => setExportFormat(e.target.value as 'csv')}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">CSV (Comma Separated)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="excel"
                      checked={exportFormat === 'excel'}
                      onChange={(e) => setExportFormat(e.target.value as 'excel')}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Excel (.xls)</span>
                  </label>
                </div>
              </div>

              {/* Column Options */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={includeOptionalColumns}
                    onChange={(e) => setIncludeOptionalColumns(e.target.checked)}
                    className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Include optional columns (Phone, Department, Specialties, License)
                  </span>
                </label>
              </div>

              {/* Preview */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Preview (first 10 rows)
                </h3>
                <div className="border border-gray-300 rounded-md overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Role</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Organization</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                        {includeOptionalColumns && (
                          <>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Phone</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Department</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">License</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewUsers.map((user, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900">{user.email}</td>
                          <td className="px-3 py-2 text-gray-900">{user.firstName} {user.lastName}</td>
                          <td className="px-3 py-2 text-gray-700">{user.role}</td>
                          <td className="px-3 py-2 text-gray-700">{user.organizationName}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                              user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          {includeOptionalColumns && (
                            <>
                              <td className="px-3 py-2 text-gray-700">{user.profile?.phoneNumber || '-'}</td>
                              <td className="px-3 py-2 text-gray-700">{user.profile?.department || '-'}</td>
                              <td className="px-3 py-2 text-gray-700">{user.practitioner?.medicalLicense || '-'}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {users.length > 10 && (
                  <p className="mt-2 text-xs text-gray-500">
                    ...and {users.length - 10} more rows
                  </p>
                )}
              </div>

              {/* Warning for large exports */}
              {users.length > 500 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Large export detected ({users.length} users). This may take a moment to process.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                disabled={exporting}
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {exporting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Export {exportFormat.toUpperCase()}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserExport;
